import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  purchaseReturnService,
  PurchaseReturn,
  PurchaseReturnStatus,
  PurchaseReturnSupplierOutcome,
  PURCHASE_RETURN_STATUS_COLORS,
  PURCHASE_RETURN_STATUS_LABELS,
} from "../../services/purchase-return.service";
import { showToast } from "../../lib/toast";

export function PurchaseReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [purchaseReturn, setPurchaseReturn] = useState<PurchaseReturn | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<Exclude<
    PurchaseReturnStatus,
    "DRAFT"
  > | null>(null);
  const [transitionReason, setTransitionReason] = useState("");
  const [reference, setReference] = useState("");
  const [supplierOutcome, setSupplierOutcome] =
    useState<PurchaseReturnSupplierOutcome>("FULL_CREDIT");
  const [outcomeAmount, setOutcomeAmount] = useState("");

  useEffect(() => {
    if (!id) return;
    const loadReturn = async () => {
      try {
        setLoading(true);
        const data = await purchaseReturnService.getPurchaseReturn(id);
        setPurchaseReturn(data);
      } catch (err: any) {
        showToast.error(
          err.response?.data?.message || "Failed to load purchase return",
        );
        navigate("/purchase-returns");
      } finally {
        setLoading(false);
      }
    };

    loadReturn();
  }, [id, navigate]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const nextStatuses: Record<PurchaseReturnStatus, PurchaseReturnStatus[]> = {
    DRAFT: ["SUBMITTED", "CANCELLED"],
    SUBMITTED: ["APPROVED", "CANCELLED"],
    APPROVED: ["RESERVED", "CANCELLED"],
    RESERVED: ["DISPATCHED", "CANCELLED"],
    DISPATCHED: ["ACKNOWLEDGED", "COMPLETED"],
    ACKNOWLEDGED: ["COMPLETED"],
    COMPLETED: [],
    CANCELLED: [],
  };

  const handleTransition = async () => {
    if (!id || !transitionTarget || !transitionReason.trim()) {
      showToast.error("Enter a reason for this status change");
      return;
    }
    if (transitionTarget === "ACKNOWLEDGED" && !reference.trim()) {
      showToast.error("Enter the supplier acknowledgement reference");
      return;
    }
    try {
      setTransitioning(true);
      const result = await purchaseReturnService.transitionPurchaseReturn(id, {
        toStatus: transitionTarget,
        reason: transitionReason.trim(),
        approvalRequestId:
          transitionTarget === "APPROVED"
            ? (purchaseReturn?.approvalRequestId ?? undefined)
            : undefined,
        reference: reference.trim() || undefined,
        supplierOutcome:
          transitionTarget === "COMPLETED" ? supplierOutcome : undefined,
        outcomeAmountPaise:
          transitionTarget === "COMPLETED" && outcomeAmount !== ""
            ? Math.round(Number(outcomeAmount) * 100)
            : undefined,
      });
      showToast.success(
        transitionTarget === "SUBMITTED" && result.approvalRequestId
          ? "Return submitted for independent review"
          : `Return moved to ${PURCHASE_RETURN_STATUS_LABELS[transitionTarget]}`,
      );
      setTransitionTarget(null);
      setTransitionReason("");
      setReference("");
      setOutcomeAmount("");
      const refreshed = await purchaseReturnService.getPurchaseReturn(id);
      setPurchaseReturn(refreshed);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to update purchase return",
      );
    } finally {
      setTransitioning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!purchaseReturn) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          Purchase return not found
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/purchase-returns")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Purchase Returns
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Return #{purchaseReturn.returnNumber}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {formatDate(purchaseReturn.returnDate)} ·{" "}
              {purchaseReturn.supplier.name}
            </p>
          </div>
          <div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${PURCHASE_RETURN_STATUS_COLORS[purchaseReturn.status]}`}
            >
              {PURCHASE_RETURN_STATUS_LABELS[purchaseReturn.status]}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Return Details
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Supplier</p>
                <p className="font-medium text-gray-900">
                  {purchaseReturn.supplier.name}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Return Reason</p>
                <p className="font-medium text-gray-900">
                  {purchaseReturn.returnReason}
                </p>
              </div>
              {purchaseReturn.goodsReceipt?.grnNumber && (
                <div>
                  <p className="text-gray-500">GRN</p>
                  <button
                    onClick={() =>
                      navigate(
                        `/goods-receipts/${purchaseReturn.goodsReceipt?.id}`,
                      )
                    }
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    {purchaseReturn.goodsReceipt.grnNumber}
                  </button>
                </div>
              )}
              {purchaseReturn.notes && (
                <div>
                  <p className="text-gray-500">Notes</p>
                  <p className="font-medium text-gray-900">
                    {purchaseReturn.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Batch
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Unit Price
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Tax
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseReturn.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-900">
                          {item.product.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.product.sku}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs font-mono">
                          {item.batch.batchNumber}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {item.qtyReturned}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.taxAmount)}
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Summary
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">
                  {formatCurrency(purchaseReturn.subtotal)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Tax</span>
                <span className="font-medium">
                  {formatCurrency(purchaseReturn.taxAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between text-base">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(purchaseReturn.totalAmount)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <div className="space-y-2">
              {nextStatuses[purchaseReturn.status].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => {
                    setTransitionTarget(
                      status as Exclude<PurchaseReturnStatus, "DRAFT">,
                    );
                  }}
                  className={`w-full rounded-lg px-4 py-2 text-sm font-medium ${
                    status === "CANCELLED"
                      ? "border border-red-300 text-red-700 hover:bg-red-50"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {PURCHASE_RETURN_STATUS_LABELS[status]}
                </button>
              ))}
              {nextStatuses[purchaseReturn.status].length === 0 && (
                <p className="text-sm text-gray-600">
                  This return is in a final state and cannot be changed.
                </p>
              )}
            </div>

            {transitionTarget && (
              <div className="mt-4 space-y-3 border-t pt-4">
                <p className="text-sm font-medium text-gray-900">
                  Move to {PURCHASE_RETURN_STATUS_LABELS[transitionTarget]}
                </p>
                <textarea
                  value={transitionReason}
                  onChange={(event) => setTransitionReason(event.target.value)}
                  placeholder="Reason for status change *"
                  maxLength={500}
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
                {transitionTarget === "ACKNOWLEDGED" && (
                  <input
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder="Supplier acknowledgement reference *"
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  />
                )}
                {transitionTarget === "COMPLETED" && (
                  <>
                    <select
                      value={supplierOutcome}
                      onChange={(event) =>
                        setSupplierOutcome(
                          event.target.value as PurchaseReturnSupplierOutcome,
                        )
                      }
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="FULL_CREDIT">Full credit</option>
                      <option value="PARTIAL_CREDIT">Partial credit</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="REPLACEMENT">Replacement</option>
                      <option value="REPAIR">Repair</option>
                      <option value="RETURN_TO_PHARMACY">
                        Returned to pharmacy
                      </option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={outcomeAmount}
                      onChange={(event) => setOutcomeAmount(event.target.value)}
                      placeholder="Credited amount (optional)"
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                  </>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setTransitionTarget(null)}
                    className="flex-1 rounded-lg border px-3 py-2 text-sm"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleTransition}
                    disabled={transitioning}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {transitioning ? "Updating..." : "Confirm"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
